import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router";
import { IInvoiceParams, InvoiceListItem } from "shared";

import { buildInvoiceListParamsFromFilterModel } from "../lib/invoiceFilterParams";
import {
  InvoiceAgingBucket,
  InvoiceAgingBucketKey,
  invoiceAgingBuckets,
} from "../lib/invoiceAgingBuckets";
import { useFetchInvoicesQuery } from "../services/api/invoiceApi/invoice";

import { useInvoiceTableModels } from "./useInvoices";

interface InvoiceAgingColumn extends InvoiceAgingBucket {
  invoices: InvoiceListItem[];
  count: number;
  outstandingTotalHomeCents: number;
  loadedCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isFetching: boolean;
  loadMore: () => void;
  error: unknown;
}

interface UseAgingColumnInvoicesArgs {
  organisationId?: string;
  contactId?: number;
  daysOverdueMin?: number;
  daysOverdueMax?: number;
  sortType: `asc` | `desc`;
  sharedFilterParams: Partial<IInvoiceParams>;
  filterSignature: string;
}

interface UseAgingColumnInvoicesResult {
  invoices: InvoiceListItem[];
  count: number;
  outstandingTotalHomeCents: number;
  loadedCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isFetching: boolean;
  loadMore: () => void;
  error: unknown;
}

const PAGE_SIZE = 100;

// Each column queries with the bounds and ordering of its shared bucket
// definition, so the rendered metadata always matches the fetched data.
function bucketQueryArgs(key: InvoiceAgingBucketKey) {
  const bucket = invoiceAgingBuckets.find(candidate => candidate.key === key);

  if (!bucket) {
    throw new Error(`Unknown invoice aging bucket: ${key}`);
  }

  return {
    daysOverdueMin: bucket.daysOverdueMin,
    daysOverdueMax: bucket.daysOverdueMax,
    sortType: bucket.sortType,
  };
}

function dedupeInvoices(invoices: InvoiceListItem[]) {
  const byId = new Map<string, InvoiceListItem>();

  invoices.forEach(invoice => {
    byId.set(invoice.id, invoice);
  });

  return Array.from(byId.values());
}

function useAgingColumnInvoices({
  organisationId,
  contactId,
  daysOverdueMin,
  daysOverdueMax,
  sortType,
  sharedFilterParams,
  filterSignature,
}: UseAgingColumnInvoicesArgs): UseAgingColumnInvoicesResult {
  const [page, setPage] = useState(1);
  const [accumulatedInvoices, setAccumulatedInvoices] = useState<InvoiceListItem[]>([]);
  const effectiveContactId = contactId ?? sharedFilterParams.contactId ?? undefined;
  const effectiveGroupId = contactId ? undefined : sharedFilterParams.groupId;

  const queryResult = useFetchInvoicesQuery({
    organisationId: organisationId || ``,
    ...sharedFilterParams,
    contactId: effectiveContactId,
    groupId: effectiveGroupId,
    page,
    limit: PAGE_SIZE,
    xeroStatus: `AUTHORISED`,
    daysOverdueMin,
    daysOverdueMax,
    sortColumn: `days_overdue`,
    sortType,
  }, {
    skip: !organisationId,
    pollingInterval: 1000 * 90,
    skipPollingIfUnfocused: true,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    setPage(1);
    setAccumulatedInvoices([]);
  }, [organisationId, contactId, daysOverdueMin, daysOverdueMax, sortType, filterSignature]);

  useEffect(() => {
    // Wait until the response matches the requested page so we don't
    // re-append stale page-1 data when page increments.
    if (queryResult.isFetching || !queryResult.isSuccess || !queryResult.data?.invoices) {
      return;
    }

    if (queryResult.originalArgs?.page !== page) {
      return;
    }

    const pageInvoices = queryResult.data.invoices;

    if (page === 1) {
      setAccumulatedInvoices(dedupeInvoices(pageInvoices));

      return;
    }

    setAccumulatedInvoices(currentInvoices => dedupeInvoices([
      ...currentInvoices,
      ...pageInvoices,
    ]));
  }, [
    page,
    queryResult.data,
    queryResult.isFetching,
    queryResult.isSuccess,
    queryResult.originalArgs?.page,
  ]);

  const count = Number(queryResult.data?.count ?? 0);
  const outstandingTotalHomeCents = Number(queryResult.data?.outstandingTotalHomeCents ?? 0);
  const loadedCount = accumulatedInvoices.length;
  const hasMore = count > loadedCount;

  function loadMore() {
    if (!hasMore || queryResult.isFetching) {
      return;
    }

    setPage(currentPage => currentPage + 1);
  }

  return {
    invoices: accumulatedInvoices,
    count,
    outstandingTotalHomeCents,
    loadedCount,
    hasMore,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    loadMore,
    error: queryResult.error,
  };
}

export function useUnpaidInvoicesBoard() {
  const { organisationId, contactId: contactIdParam } = useParams<{ organisationId: string; contactId?: string }>();
  const { filterModel } = useInvoiceTableModels();
  const contactId = contactIdParam ? Number(contactIdParam) : undefined;
  const sharedFilterParams = useMemo<Partial<IInvoiceParams>>(() => {
    const invoiceListParams = buildInvoiceListParamsFromFilterModel(filterModel);
    // Board owns status/aging/sort/paging; strip list-only params from shared filters.
    const restFilterParams = { ...invoiceListParams };
    delete restFilterParams.xeroStatus;
    delete restFilterParams.daysOverdue;
    delete restFilterParams.daysOverdueOperator;
    delete restFilterParams.daysOverdueMin;
    delete restFilterParams.daysOverdueMax;
    delete restFilterParams.sortColumn;
    delete restFilterParams.sortType;
    delete restFilterParams.page;
    delete restFilterParams.limit;

    return restFilterParams;
  }, [filterModel]);
  const filterSignature = useMemo(
    () => JSON.stringify(sharedFilterParams),
    [sharedFilterParams],
  );

  const currentColumn = useAgingColumnInvoices({
    organisationId,
    contactId,
    ...bucketQueryArgs(`current`),
    sharedFilterParams,
    filterSignature,
  });
  const days1To30Column = useAgingColumnInvoices({
    organisationId,
    contactId,
    ...bucketQueryArgs(`days_1_30`),
    sharedFilterParams,
    filterSignature,
  });
  const days31To60Column = useAgingColumnInvoices({
    organisationId,
    contactId,
    ...bucketQueryArgs(`days_31_60`),
    sharedFilterParams,
    filterSignature,
  });
  const days61To90Column = useAgingColumnInvoices({
    organisationId,
    contactId,
    ...bucketQueryArgs(`days_61_90`),
    sharedFilterParams,
    filterSignature,
  });
  const days90PlusColumn = useAgingColumnInvoices({
    organisationId,
    contactId,
    ...bucketQueryArgs(`days_90_plus`),
    sharedFilterParams,
    filterSignature,
  });

  const columns = useMemo<InvoiceAgingColumn[]>(() => {
    const columnsByKey: Record<InvoiceAgingBucketKey, UseAgingColumnInvoicesResult> = {
      current: currentColumn,
      days_1_30: days1To30Column,
      days_31_60: days31To60Column,
      days_61_90: days61To90Column,
      days_90_plus: days90PlusColumn,
    };

    return invoiceAgingBuckets.map(column => ({
      ...column,
      invoices: columnsByKey[column.key].invoices,
      count: columnsByKey[column.key].count,
      outstandingTotalHomeCents: columnsByKey[column.key].outstandingTotalHomeCents,
      loadedCount: columnsByKey[column.key].loadedCount,
      hasMore: columnsByKey[column.key].hasMore,
      isLoading: columnsByKey[column.key].isLoading,
      isFetching: columnsByKey[column.key].isFetching,
      loadMore: columnsByKey[column.key].loadMore,
      error: columnsByKey[column.key].error,
    }));
  }, [
    currentColumn,
    days1To30Column,
    days31To60Column,
    days61To90Column,
    days90PlusColumn,
  ]);

  const error = columns.find(column => column.error)?.error;

  return {
    columns,
    isLoading: columns.some(column => column.isLoading),
    isFetching: columns.some(column => column.isFetching),
    error,
  };
}
